import { Modal, Button } from 'antd';
import React, { useEffect } from 'react';
import { MarkdownRenderInEditor } from './markdownRender';
import { useTranslation } from 'react-i18next';
import type { Announcement } from '@/api/admin';

const LAST_USER_NOTIFICATION_KEY = 'lastUserNewsNotification';
const LAST_GLOBAL_NOTIFICATION_KEY = 'lastGlobalNewsNotification';

interface AnnouncementNotificationProps {
  userAnnouncement: Announcement | null;
  globalAnnouncement: Announcement | null;
  onClose: () => void;
}

const AnnouncementNotification: React.FC<AnnouncementNotificationProps> = ({
  userAnnouncement,
  globalAnnouncement,
  onClose,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [pages, setPages] = React.useState<Announcement[]>([]);
  const [currentPage, setCurrentPage] = React.useState(0);
  const { t } = useTranslation('common');

  useEffect(() => {
    const lastUser = localStorage.getItem(LAST_USER_NOTIFICATION_KEY);
    const lastGlobal = localStorage.getItem(LAST_GLOBAL_NOTIFICATION_KEY);

    const newPages: Announcement[] = [];

    // User-specific announcement goes first
    if (userAnnouncement && lastUser !== userAnnouncement.updated_at) {
      newPages.push(userAnnouncement);
    }

    if (globalAnnouncement && lastGlobal !== globalAnnouncement.updated_at) {
      newPages.push(globalAnnouncement);
    }

    if (newPages.length > 0) {
      setPages(newPages);
      setCurrentPage(0);
      setIsOpen(true);
    }
  }, [userAnnouncement, globalAnnouncement]);

  const handleClose = () => {
    if (userAnnouncement && pages.some((p) => p.id === userAnnouncement.id)) {
      localStorage.setItem(LAST_USER_NOTIFICATION_KEY, userAnnouncement.updated_at);
    }
    if (globalAnnouncement && pages.some((p) => p.id === globalAnnouncement.id)) {
      localStorage.setItem(LAST_GLOBAL_NOTIFICATION_KEY, globalAnnouncement.updated_at);
    }
    setIsOpen(false);
    onClose();
  };

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage((prev) => prev + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  if (pages.length === 0) return null;

  const current = pages[currentPage];
  const isLast = currentPage === pages.length - 1;
  const isFirst = currentPage === 0;

  return (
    <Modal
      title={
        <div className="flex items-center gap-4">
          <span className="text-xl font-semibold text-gray-800 dark:text-[#CCCCCC]">{current.title}</span>
          {pages.length > 1 && (
            <span className="text-sm font-normal text-gray-400 dark:text-[#858585]">
              {currentPage + 1} / {pages.length}
            </span>
          )}
        </div>
      }
      open={isOpen}
      onCancel={handleClose}
      footer={
        <div className="flex justify-end gap-2">
          {pages.length > 1 && !isFirst && (
            <Button onClick={handlePrev}>{t('announcement.prev')}</Button>
          )}
          <Button type="primary" onClick={handleNext}>
            {isLast ? t('announcement.ok') : t('announcement.next')}
          </Button>
        </div>
      }
      width={800}
      centered
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: '24px',
        },
      }}
    >
      <MarkdownRenderInEditor content={current.content} />
    </Modal>
  );
};

export default AnnouncementNotification;
